import React, { useState, useEffect, useMemo } from "react";
import { intl, fetchData } from "dmsbasereact";
import { ReportLayout } from "./components/ReportLayout";
import { useFormatters } from "../../hooks/useFormater";

/**
 * Generate dynamic institution columns based on max_institutions count
 */
const generateInstitutionColumns = (maxInstitutions, formatters) => {
    const columns = [];

    for (let i = 1; i <= maxInstitutions; i++) {
        // Use parameterized translations for scalability (works with unlimited institutions)
        const nameLabel = intl.get("Reports.Doctors.InstitutionName", { number: i }).d(`Prescribing Institution {number}`);
        const cnpjLabel = intl.get("Reports.Doctors.InstitutionCNPJ", { number: i }).d(`Inst. {number} CNPJ`);
        const addressLabel = intl.get("Reports.Doctors.InstitutionAddress", { number: i }).d(`Inst. {number} Address`);
        const cityLabel = intl.get("Reports.Doctors.InstitutionCity", { number: i }).d(`Inst. {number} City`);
        const stateLabel = intl.get("Reports.Doctors.InstitutionState", { number: i }).d(`Inst. {number} State`);

        columns.push(
            {
                label: nameLabel,
                column_name: `institution_${i}_name`,
                width: "180px",
            },
            {
                label: cnpjLabel,
                column_name: `institution_${i}_cnpj`,
                width: "140px",
                function: (value) => formatters.formatCNPJ(value),
            },
            {
                label: addressLabel,
                column_name: `institution_${i}_address`,
                width: "180px",
            },
            {
                label: cityLabel,
                column_name: `institution_${i}_city`,
                width: "120px",
            },
            {
                label: stateLabel,
                column_name: `institution_${i}_state`,
                width: "60px",
            }
        );
    }

    return columns;
};

export const DoctorsReportPage = () => {
    const formatters = useFormatters();
    const [maxInstitutions, setMaxInstitutions] = useState(1);
    const [isLoading, setIsLoading] = useState(true);

    // Fetch max_institutions from filter-options
    useEffect(() => {
        fetchData("/reports/doctors/filter-options", "GET", true)
            .then((response) => {
                const data = response?.data?.data || response?.data || {};
                const max = data.max_institutions || 1;
                setMaxInstitutions(max);
                setIsLoading(false);
            })
            .catch((error) => {
                console.error("Failed to fetch filter options:", error);
                setIsLoading(false);
            });
    }, []);

    // Build header dynamically based on max_institutions
    const header = useMemo(() => {
        const baseColumns = [
            {
                label: intl.get("Reports.Doctors.Name").d("Doctor Name"),
                column_name: "name",
                width: "180px",
            },
            {
                label: intl.get("Reports.Doctors.CRM").d("CRM/UF"),
                column_name: "crm",
                width: "120px",
            },
            {
                label: intl.get("Reports.Doctors.AdditionalCRMs").d("Additional CRMs"),
                column_name: "additional_crms",
                width: "180px",
            },
            {
                label: intl.get("Reports.Doctors.Email").d("Email"),
                column_name: "email",
                width: "200px",
            },
            {
                label: intl.get("Reports.Doctors.Phone").d("Phone"),
                column_name: "phone",
                width: "120px",
                function: (value) => formatters.formatPhone(value),
            },
            {
                label: intl.get("Reports.Doctors.TrainingDate").d("Training Date"),
                column_name: "training_date",
                width: "120px",
            },
            {
                label: intl.get("Reports.Doctors.TrainingDateLatest").d("Latest Training Date"),
                column_name: "training_date_latest",
                width: "130px",
            },
            {
                label: intl.get("Reports.Doctors.CreatedOn").d("User Created Date"),
                column_name: "created_on",
                width: "110px",
            },
        ];

        const institutionColumns = generateInstitutionColumns(maxInstitutions, formatters);

        return [...baseColumns, ...institutionColumns];
    }, [maxInstitutions, formatters]);

    const filterFields = [
        { name: "name", label: intl.get("Name").d("Name"), type: "text" },
        { name: "crm", label: intl.get("CRM").d("CRM"), type: "text" },
        {
            name: "crm_state",
            label: intl.get("Reports.Doctors.FilterCRMState").d("CRM State"),
            type: "multiselect",
            optionsKey: "crm_state",
        },
        { name: "institution_name", label: intl.get("Reports.Doctors.FilterInstitutionName").d("Institution Name"), type: "text" },
        { name: "institution_cnpj", label: intl.get("Reports.Doctors.FilterInstitutionCNPJ").d("Institution CNPJ"), type: "text" },
    ];

    if (isLoading) {
        return null; // Or a loading spinner
    }

    return (
        <ReportLayout
            title="Reports.Doctors.PageTitle"
            titleDefault="Prescribers Report"
            manualSlug="reports-doctors"
            tableUrl="/reports/doctors/table"
            filterOptionsUrl="/reports/doctors/filter-options"
            exportUrl="/reports/doctors/export"
            header={header}
            filterFields={filterFields}
            dateFilterLabel={intl.get("Reports.DateFilter.RegistrationDate").d("Registration Date")}
        />
    );
};

export default DoctorsReportPage;
